/**
 * Tealibere Dynamic Personalization Engine
 * ─────────────────────────────────────────
 * B3.2 — Smart cart recs, recently viewed, visitor detection,
 *         shipping progress, cross-sell intelligence
 *
 * Loads after DOM; zero render-blocking. All data in localStorage.
 */
(function () {
  'use strict';

  /* ══════════════════════════════════════════════
     CONFIG
     ══════════════════════════════════════════════ */
  var CFG = {
    FREE_SHIP_THRESHOLD: 6900,            // cents — $69.00
    MAX_RECENT: 8,                        // max recently-viewed products
    LS_RECENT: 'tl_recently_viewed',
    LS_VISITS: 'tl_visit_count',
    LS_FIRST:  'tl_first_visit',
    CROSS_SELL_MAP: {
      'tea-cup':    ['teapot', 'gaiwan', 'tea-tray', 'tea'],
      'teapot':     ['tea-cup', 'gaiwan', 'tea-tray', 'tea'],
      'gaiwan':     ['tea-cup', 'teapot', 'tea-tray', 'tea'],
      'tea-tray':   ['teapot', 'gaiwan', 'tea-cup'],
      'tea':        ['teapot', 'tea-cup', 'gaiwan'],
      'tea-set':    ['tea', 'tea-tray', 'tea-pet'],
      'tea-pet':    ['tea-set', 'tea-tray', 'teapot'],
      'default':    ['teapot', 'tea-cup', 'gaiwan', 'tea']
    }
  };

  /* ══════════════════════════════════════════════
     1. RECENTLY VIEWED PRODUCTS
     ══════════════════════════════════════════════ */
  var RecentlyViewed = {
    _key: CFG.LS_RECENT,

    get: function () {
      try {
        return JSON.parse(localStorage.getItem(this._key)) || [];
      } catch (e) { return []; }
    },

    track: function () {
      // Only on product pages
      var meta = document.querySelector('meta[property="og:type"][content="product"]');
      if (!meta) return;

      var handle = window.location.pathname.split('/products/')[1];
      if (!handle) return;
      handle = handle.split('?')[0].split('#')[0];

      var product = {
        handle: handle,
        title: (document.querySelector('meta[property="og:title"]') || {}).content || '',
        image: (document.querySelector('meta[property="og:image"]') || {}).content || '',
        price: (document.querySelector('meta[property="product:price:amount"]') || {}).content || '',
        url:   '/products/' + handle,
        ts:    Date.now()
      };

      var items = this.get().filter(function (p) { return p.handle !== handle; });
      items.unshift(product);
      if (items.length > CFG.MAX_RECENT) items = items.slice(0, CFG.MAX_RECENT);

      try { localStorage.setItem(this._key, JSON.stringify(items)); } catch (e) {}
    },

    /** Render into any container with data-recently-viewed attribute */
    render: function () {
      var containers = document.querySelectorAll('[data-recently-viewed]');
      if (!containers.length) return;

      var items = this.get();
      if (items.length < 2) return; // need at least 2 to be useful

      var currentHandle = '';
      var path = window.location.pathname;
      if (path.indexOf('/products/') !== -1) {
        currentHandle = path.split('/products/')[1].split('?')[0].split('#')[0];
      }

      var filtered = items.filter(function (p) { return p.handle !== currentHandle; });
      if (!filtered.length) return;

      containers.forEach(function (el) {
        var limit = parseInt(el.dataset.recentlyViewedLimit) || 4;
        var show = filtered.slice(0, limit);

        el.innerHTML =
          '<div class="tl-rv-grid">' +
          show.map(function (p) {
            return '<a href="' + p.url + '" class="tl-rv-card">' +
              (p.image ? '<img src="' + p.image + '&width=300" alt="' + p.title + '" loading="lazy" width="300" height="300">' : '') +
              '<span class="tl-rv-title">' + p.title + '</span>' +
              (p.price ? '<span class="tl-rv-price">$' + p.price + '</span>' : '') +
              '</a>';
          }).join('') +
          '</div>';

        el.style.display = '';
      });
    }
  };

  /* ══════════════════════════════════════════════
     2. VISITOR DETECTION (New vs Returning)
     ══════════════════════════════════════════════ */
  var Visitor = {
    init: function () {
      var count = parseInt(localStorage.getItem(CFG.LS_VISITS)) || 0;
      count++;
      try {
        localStorage.setItem(CFG.LS_VISITS, count);
        if (!localStorage.getItem(CFG.LS_FIRST)) {
          localStorage.setItem(CFG.LS_FIRST, Date.now());
        }
      } catch (e) {}
      return count;
    },

    isReturning: function () {
      return (parseInt(localStorage.getItem(CFG.LS_VISITS)) || 0) > 1;
    },

    getVisitCount: function () {
      return parseInt(localStorage.getItem(CFG.LS_VISITS)) || 1;
    },

    /**
     * Show/hide elements based on visitor type.
     * [data-show-new]       → only visible to first-time visitors
     * [data-show-returning] → only visible to returning visitors
     */
    applyVisibility: function () {
      var returning = this.isReturning();
      document.querySelectorAll('[data-show-new]').forEach(function (el) {
        el.style.display = returning ? 'none' : '';
      });
      document.querySelectorAll('[data-show-returning]').forEach(function (el) {
        el.style.display = returning ? '' : 'none';
      });
    }
  };

  /* ══════════════════════════════════════════════
     3. FREE SHIPPING PROGRESS BAR
     ══════════════════════════════════════════════ */
  var ShippingProgress = {
    /**
     * Renders a progress bar inside [data-shipping-progress].
     * Reads cart total from Shopify Ajax API.
     */
    render: function () {
      var containers = document.querySelectorAll('[data-shipping-progress]');
      if (!containers.length) return;

      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var total = cart.total_price; // cents
          var threshold = CFG.FREE_SHIP_THRESHOLD;
          var remaining = threshold - total;
          var pct = Math.min(100, Math.round((total / threshold) * 100));

          var msg, barClass;
          if (remaining <= 0) {
            msg = '🎉 You qualify for <strong>FREE shipping</strong>!';
            barClass = 'tl-ship-bar--full';
          } else {
            var dollars = (remaining / 100).toFixed(2);
            msg = 'Add <strong>$' + dollars + '</strong> more for <strong>FREE shipping</strong>';
            barClass = '';
          }

          containers.forEach(function (el) {
            el.innerHTML =
              '<div class="tl-ship-msg">' + msg + '</div>' +
              '<div class="tl-ship-track">' +
                '<div class="tl-ship-bar ' + barClass + '" style="width:' + pct + '%"></div>' +
              '</div>';
            el.style.display = '';
          });
        })
        .catch(function () {});
    }
  };

  /* ══════════════════════════════════════════════
     4. SMART CART RECOMMENDATIONS
     ══════════════════════════════════════════════ */
  var CartRecs = {
    /**
     * Analyze cart contents and recommend complementary products.
     * Renders into [data-cart-recs] containers.
     */
    render: function () {
      var containers = document.querySelectorAll('[data-cart-recs]');
      if (!containers.length) return;

      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart.items || !cart.items.length) return;

          // Collect product types in cart
          var cartTypes = {};
          var cartHandles = {};
          cart.items.forEach(function (item) {
            var type = (item.product_type || '').toLowerCase().replace(/\s+/g, '-');
            if (type) cartTypes[type] = true;
            cartHandles[item.handle] = true;
          });

          // Determine recommended types
          var recTypes = {};
          Object.keys(cartTypes).forEach(function (type) {
            var mapped = CFG.CROSS_SELL_MAP[type] || CFG.CROSS_SELL_MAP['default'];
            mapped.forEach(function (t) {
              if (!cartTypes[t]) recTypes[t] = (recTypes[t] || 0) + 1;
            });
          });

          // Sort by relevance
          var sorted = Object.keys(recTypes).sort(function (a, b) {
            return recTypes[b] - recTypes[a];
          });

          if (!sorted.length) return;

          // Fetch recommended collection
          var topType = sorted[0];
          var collectionMap = {
            'teapot': 'teapots',
            'tea-cup': 'tea-cups',
            'gaiwan': 'handmade-gaiwan',
            'tea-tray': 'tea-tray',
            'tea': 'teas-organic-loose-leaf-tea',
            'tea-set': 'tea-sets-gongfu-tea-ceremony-kit',
            'tea-pet': 'tea-pets'
          };

          var collHandle = collectionMap[topType] || 'teaware';

          fetch('/collections/' + collHandle + '/products.json?limit=4')
            .then(function (r) { return r.json(); })
            .then(function (data) {
              var products = (data.products || []).filter(function (p) {
                return !cartHandles[p.handle];
              }).slice(0, 4);

              if (!products.length) return;

              containers.forEach(function (el) {
                el.innerHTML =
                  '<h3 class="tl-recs-heading">Complete Your Tea Ritual</h3>' +
                  '<div class="tl-recs-grid">' +
                  products.map(function (p) {
                    var img = (p.images && p.images[0]) ? p.images[0].src + '&width=300' : '';
                    var price = p.variants && p.variants[0] ? p.variants[0].price : '';
                    return '<a href="/products/' + p.handle + '" class="tl-recs-card">' +
                      (img ? '<img src="' + img + '" alt="' + p.title + '" loading="lazy" width="300" height="300">' : '') +
                      '<span class="tl-recs-title">' + p.title + '</span>' +
                      '<span class="tl-recs-price">$' + price + '</span>' +
                      '</a>';
                  }).join('') +
                  '</div>';
                el.style.display = '';
              });
            })
            .catch(function () {});
        })
        .catch(function () {});
    }
  };

  /* ══════════════════════════════════════════════
     5. PRODUCT PAGE CROSS-SELL INTELLIGENCE
     ══════════════════════════════════════════════ */
  /* ═══════════════════════════════════════════════
     Module 8 — Collection Category Navigation
     Injects a detailed category nav bar on /collections/all
     ══════════════════════════════════════════════ */
  var CollectionNav = {
    CATEGORIES: [
      {
        label: 'Teaware',
        children: [
          { name: 'Teapots', handle: 'handmade-mysticteascroll-teapots', icon: '🫖' },
          { name: 'Tea Cups', handle: 'handmade-jian-zhan-mysticteascroll-tea-cups', icon: '🍵' },
          { name: 'Tea Sets', handle: 'tea-sets-gongfu-tea-ceremony-kit', icon: '☕' },
          { name: 'Gaiwan', handle: 'handmade-gaiwan', icon: '🏺' },
          { name: 'Coffee Cups', handle: 'raindrops-jian-zhan-coffee-cup', icon: '☕' },
          { name: 'Tea Pitcher', handle: 'tea-pitcher', icon: '🏺' },
          { name: 'Tea Tray', handle: 'tea-tray', icon: '🪵' },
          { name: 'Accessories', handle: 'accessories', icon: '🔧' }
        ]
      },
      {
        label: 'By Craft',
        children: [
          { name: 'Tenmoku', handle: 'handmade-tenmoku', icon: '✨' },
          { name: 'Yixing', handle: 'yixing-teaware', icon: '🏺' },
          { name: 'Dehua Porcelain', handle: 'dehua-white-porcelain', icon: '🤍' },
          { name: 'Jingdezhen', handle: 'jingdezhen-porcelain', icon: '🎨' },
          { name: 'Jianshui', handle: 'jianshui', icon: '🟤' },
          { name: 'Glass', handle: 'glass', icon: '🔮' }
        ]
      },
      {
        label: 'Tea',
        children: [
          { name: 'All Tea', handle: 'teas-organic-loose-leaf-tea', icon: '🍃' },
          { name: 'Green Tea', handle: 'green-tea', icon: '🟢' },
          { name: 'Black Tea', handle: 'black-tea', icon: '⚫' },
          { name: 'Oolong Tea', handle: 'oolong-tea', icon: '🟠' },
          { name: 'Pu-erh Tea', handle: 'pu-erh-tea', icon: '🟤' },
          { name: 'White Tea', handle: 'white-tea', icon: '⚪' },
          { name: 'Herbal & Wellness', handle: 'herbal-wellness-tea', icon: '🌿' },
          { name: 'Spring 2025', handle: '2025-spring-tea', icon: '🌸' }
        ]
      },
      {
        label: 'Tea Pets',
        children: [
          { name: 'All Tea Pets', handle: 'tea-pets', icon: '🐉' },
          { name: 'Wealth', handle: 'wealth-tea-pets', icon: '💰' },
          { name: 'Career', handle: 'career-tea-pets', icon: '🏆' },
          { name: 'Love', handle: 'love-tea-pets', icon: '❤️' },
          { name: 'Health', handle: 'health-tea-pets', icon: '💚' },
          { name: 'Meditation', handle: 'meditation-tea-pets', icon: '🧘' }
        ]
      }
    ],

    render: function () {
      // Only inject on /collections/all or /collections page
      var path = window.location.pathname.replace(/\/+$/, '');
      if (path !== '/collections/all' && path !== '/collections') return;

      // Find injection point — the shopify section containing the product grid
      var grid = document.getElementById('shopify-section-product-grid');
      if (!grid) {
        grid = document.querySelector('[id*="product-grid"].shopify-section, .shopify-section .collection.page-width');
        if (grid && grid.classList.contains('page-width')) grid = grid.closest('.shopify-section');
      }
      if (!grid) {
        // Last resort: find the section with toolbar/filter
        var toolbar = document.getElementById('ProductGridWrapper') || document.getElementById('ProductCount');
        if (toolbar) grid = toolbar.closest('.shopify-section') || toolbar.closest('[class*="section--padding"]');
      }
      if (!grid) return;

      // Don't double-inject
      if (document.querySelector('.tl-colnav')) return;

      var nav = document.createElement('div');
      nav.className = 'tl-colnav';

      var inner = '<div class="tl-colnav-inner page-width">';
      inner += '<div class="tl-colnav-tabs" role="tablist">';

      this.CATEGORIES.forEach(function (cat, i) {
        inner += '<button class="tl-colnav-tab' + (i === 0 ? ' tl-colnav-tab--active' : '') + '"' +
          ' role="tab" data-cattab="' + i + '" aria-selected="' + (i === 0) + '">' +
          cat.label + '</button>';
      });
      inner += '</div>';

      // Panels
      var cats = this.CATEGORIES;
      cats.forEach(function (cat, i) {
        inner += '<div class="tl-colnav-panel' + (i === 0 ? ' tl-colnav-panel--active' : '') + '"' +
          ' data-catpanel="' + i + '" role="tabpanel">';
        inner += '<div class="tl-colnav-links">';
        cat.children.forEach(function (c) {
          inner += '<a href="/collections/' + c.handle + '" class="tl-colnav-link">' +
            '<span class="tl-colnav-icon">' + c.icon + '</span>' +
            '<span class="tl-colnav-name">' + c.name + '</span>' +
            '</a>';
        });
        inner += '</div></div>';
      });

      inner += '</div>';
      nav.innerHTML = inner;

      // Insert before the grid
      grid.parentNode.insertBefore(nav, grid);

      // Tab switching
      nav.querySelectorAll('.tl-colnav-tab').forEach(function (tab) {
        tab.addEventListener('click', function () {
          var idx = this.dataset.cattab;
          nav.querySelectorAll('.tl-colnav-tab').forEach(function (t) {
            t.classList.remove('tl-colnav-tab--active');
            t.setAttribute('aria-selected', 'false');
          });
          this.classList.add('tl-colnav-tab--active');
          this.setAttribute('aria-selected', 'true');
          nav.querySelectorAll('.tl-colnav-panel').forEach(function (p) {
            p.classList.remove('tl-colnav-panel--active');
          });
          nav.querySelector('[data-catpanel="' + idx + '"]').classList.add('tl-colnav-panel--active');
        });
      });
    }
  };

  /* ═══════════════════════════════════════════════
     Module 9 — Cross-Sell
     ══════════════════════════════════════════════ */
  var CrossSell = {
    /**
     * On product pages, fetch complementary products based on product type.
     * Renders into [data-cross-sell] containers.
     */
    render: function () {
      var containers = document.querySelectorAll('[data-cross-sell]');
      if (!containers.length) return;

      // Get current product type
      var typeEl = document.querySelector('[data-product-type]');
      var productType = typeEl ? typeEl.dataset.productType : '';
      if (!productType) {
        // Fallback: try JSON-LD
        var ld = document.querySelector('script[type="application/ld+json"]');
        if (ld) {
          try {
            var json = JSON.parse(ld.textContent);
            productType = json.category || json.productType || '';
          } catch (e) {}
        }
      }

      var type = productType.toLowerCase().replace(/\s+/g, '-');
      var mapped = CFG.CROSS_SELL_MAP[type] || CFG.CROSS_SELL_MAP['default'];
      if (!mapped.length) return;

      var collectionMap = {
        'teapot': 'teapots',
        'tea-cup': 'tea-cups',
        'gaiwan': 'handmade-gaiwan',
        'tea-tray': 'tea-tray',
        'tea': 'teas-organic-loose-leaf-tea',
        'tea-set': 'tea-sets-gongfu-tea-ceremony-kit',
        'tea-pet': 'tea-pets'
      };

      var collHandle = collectionMap[mapped[0]] || 'teaware';
      var currentHandle = window.location.pathname.split('/products/')[1];
      if (currentHandle) currentHandle = currentHandle.split('?')[0].split('#')[0];

      fetch('/collections/' + collHandle + '/products.json?limit=5')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data.products || []).filter(function (p) {
            return p.handle !== currentHandle;
          }).slice(0, 4);

          if (!products.length) return;

          containers.forEach(function (el) {
            var heading = el.dataset.crossSellHeading || 'You Might Also Like';
            el.innerHTML =
              '<h3 class="tl-xs-heading">' + heading + '</h3>' +
              '<div class="tl-xs-grid">' +
              products.map(function (p) {
                var img = (p.images && p.images[0]) ? p.images[0].src + '&width=300' : '';
                var price = p.variants && p.variants[0] ? p.variants[0].price : '';
                return '<a href="/products/' + p.handle + '" class="tl-xs-card">' +
                  (img ? '<img src="' + img + '" alt="' + p.title + '" loading="lazy" width="300" height="300">' : '') +
                  '<span class="tl-xs-title">' + p.title + '</span>' +
                  '<span class="tl-xs-price">$' + price + '</span>' +
                  '</a>';
              }).join('') +
              '</div>';
            el.style.display = '';
          });
        })
        .catch(function () {});
    }
  };

  /* ══════════════════════════════════════════════
     6. HOMEPAGE PERSONALIZATION
     ══════════════════════════════════════════════ */
  var HomePersonalize = {
    /**
     * For returning visitors: show "Welcome Back" + Recently Viewed.
     * For new visitors: show "Bestsellers" CTA.
     */
    apply: function () {
      if (document.body.className.indexOf('index-template') === -1) return;

      var welcomeEl = document.querySelector('[data-welcome-personalize]');
      if (!welcomeEl) return;

      if (Visitor.isReturning()) {
        var recent = RecentlyViewed.get();
        if (recent.length > 0) {
          welcomeEl.innerHTML =
            '<div class="tl-welcome-back">' +
              '<h3>Welcome Back! 🍵</h3>' +
              '<p>Continue where you left off</p>' +
            '</div>';
          welcomeEl.style.display = '';
        }
      } else {
        welcomeEl.innerHTML =
          '<div class="tl-welcome-new">' +
            '<h3>New to Chinese Tea?</h3>' +
            '<p>Start with our <a href="/collections/bestsellers">Bestsellers</a> — curated for beginners.</p>' +
          '</div>';
        welcomeEl.style.display = '';
      }
    }
  };

  /* ══════════════════════════════════════════════
     7. URGENCY & SOCIAL PROOF MICRO-INTERACTIONS
     ══════════════════════════════════════════════ */
  var Urgency = {
    /**
     * Low stock badge enhancement.
     * Looks for inventory quantity in product JSON and adds urgency.
     */
    apply: function () {
      var containers = document.querySelectorAll('[data-urgency-stock]');
      if (!containers.length) return;

      containers.forEach(function (el) {
        var qty = parseInt(el.dataset.urgencyStock);
        if (isNaN(qty) || qty > 5) {
          el.style.display = 'none';
          return;
        }
        if (qty <= 0) {
          el.textContent = 'Sold Out';
          el.className += ' tl-urgency--sold';
        } else if (qty <= 3) {
          el.textContent = 'Only ' + qty + ' left — order soon!';
          el.className += ' tl-urgency--low';
        } else {
          el.textContent = 'Limited stock: ' + qty + ' remaining';
          el.className += ' tl-urgency--med';
        }
        el.style.display = '';
      });
    }
  };

  /* ══════════════════════════════════════════════
     INIT — Run after DOM ready
     ══════════════════════════════════════════════ */
  function init() {
    // Track visit
    Visitor.init();

    // Track product view
    RecentlyViewed.track();

    // Apply visitor-based visibility
    Visitor.applyVisibility();

    // Render components
    RecentlyViewed.render();
    ShippingProgress.render();
    HomePersonalize.apply();
    Urgency.apply();

    // Cart-dependent features (only on cart page or via drawer)
    if (window.location.pathname.indexOf('/cart') !== -1) {
      CartRecs.render();
    }

    // Product page cross-sell
    if (document.body.className.indexOf('product-template') !== -1) {
      CrossSell.render();
    }

    // Collection all page: inject category navigation
    CollectionNav.render();

    // Fix "View All Teaware" link on product pages → /collections/all
    if (window.location.pathname.indexOf('/products/') !== -1) {
      document.querySelectorAll('a[href*="/collections/teaware"]').forEach(function(a) {
        var text = (a.textContent || '').trim().toLowerCase();
        if (text.indexOf('view all') !== -1 || text.indexOf('view all teaware') !== -1) {
          a.href = '/collections/all';
          a.textContent = 'View All Products';
        }
      });
    }

    // Listen for cart updates (Shopify section rendering API)
    document.addEventListener('cart:updated', function () {
      ShippingProgress.render();
      CartRecs.render();
    });

    // Also listen for section:load in theme editor
    document.addEventListener('shopify:section:load', function () {
      RecentlyViewed.render();
      ShippingProgress.render();
      Urgency.apply();
      Visitor.applyVisibility();
    });
  }

  // Fire on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use (e.g., AJAX mini-cart, section rendering)
  window.TealiberePersonalization = {
    RecentlyViewed: RecentlyViewed,
    Visitor: Visitor,
    ShippingProgress: ShippingProgress,
    CartRecs: CartRecs,
    CrossSell: CrossSell,
    CollectionNav: CollectionNav,
    refresh: function () {
      RecentlyViewed.render();
      ShippingProgress.render();
      CartRecs.render();
      Visitor.applyVisibility();
    }
  };

})();
